import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box
} from "@mui/material";
import LaddaButton from "../../buttons/laddaButton";
import {
  WALLET_PRODUCT_ERROR_CODE,
  normalizeWalletUploadErrorForModal
} from "../../../screens/wallet/walletUploadErrorUtils";

const motivoAmigable = (motivo) => {
  if (!motivo) return "—";
  const m = String(motivo);
  if (m.includes("zvshdmtx_plus") || m.toLowerCase().includes("no existe")) {
    return "No existe el producto en la base de datos";
  }
  if (m.toLowerCase().includes("línea") || m.toLowerCase().includes("line_id")) {
    return "El producto no tiene línea de negocio asignada";
  }
  if (m.toLowerCase().includes("numérico") || m.toLowerCase().includes("código no válido")) {
    return "Código no válido en el Excel";
  }
  if (m.toLowerCase().includes("consecutivo")) return m;
  return m;
};

/**
 * Modal de ayuda cuando falla la carga de cartera (productos, consecutivos u otros errores de validación).
 */
const WalletProductErrorsModal = ({ open, onClose, errorData }) => {
  if (!open || !errorData) return null;

  const normalized = normalizeWalletUploadErrorForModal(errorData) || errorData;
  const isProductError =
    normalized.modalType === "product" ||
    normalized.errorCode === WALLET_PRODUCT_ERROR_CODE;

  const porArchivo = normalized.porArchivo || {};
  const archivos = Object.keys(porArchivo);
  const count =
    normalized.count ??
    archivos.reduce((n, k) => n + (porArchivo[k]?.length || 0), 0) ??
    normalized.details?.length ??
    0;

  const hasTable = archivos.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="wallet-product-errors-title"
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          borderRadius: 2
        }
      }}
    >
      <DialogTitle id="wallet-product-errors-title" sx={{ pb: 0 }}>
        <Typography variant="h6" component="span" color="error.main">
          Error al cargar cartera
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {normalized.message || normalized.error || "La carga no se completó."}
        </Typography>

        {isProductError && count > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Resumen: <strong>{count}</strong> producto(s) con error. Si son productos nuevos,
            ingresa al administrador de productos e insértalos; también puedes consultar si ya
            existen o corregir los códigos en el Excel y volver a intentar.
          </Typography>
        )}

        {normalized.accion && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Qué hacer:</strong> {normalized.accion}
          </Typography>
        )}

        {normalized.lineName && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Línea:</strong> {normalized.lineName}
          </Typography>
        )}

        {Array.isArray(normalized.missing) && normalized.missing.length > 0 && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Faltan:</strong> {normalized.missing.join(", ")}
          </Typography>
        )}

        {hasTable &&
          archivos.map((nombreArchivo) => {
            const items = porArchivo[nombreArchivo] || [];
            return (
              <Box key={nombreArchivo} sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: "primary.main", mb: 1 }}
                >
                  {nombreArchivo} ({items.length} error{items.length !== 1 ? "es" : ""})
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 340 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Archivo</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Celda</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Motivo</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((row, idx) => (
                        <TableRow key={`${nombreArchivo}-${idx}-${row.codigo}-${row.celda}`}>
                          <TableCell>{row.archivo ?? nombreArchivo}</TableCell>
                          <TableCell>{String(row.codigo ?? "")}</TableCell>
                          <TableCell>{row.nombre ?? "—"}</TableCell>
                          <TableCell>{row.celda || "—"}</TableCell>
                          <TableCell sx={{ maxWidth: 260 }}>{motivoAmigable(row.motivo)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })}

        {!hasTable && !isProductError && (
          <Typography variant="body2" color="text.secondary">
            Revise el mensaje anterior, corrija los archivos y vuelva a intentar.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <LaddaButton variant="contained" onClick={onClose}>
          <div className="HContainer align-center">
            <p className="button_text">Entendido</p>
          </div>
        </LaddaButton>
      </DialogActions>
    </Dialog>
  );
};

export default WalletProductErrorsModal;
